import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const STORAGE_KEY = "contentFontScale";
const SCALE_VALUES = [1, 1.15, 1.3];
const SCALE_LABELS = {
  1: "Default",
  1.15: "Large",
  1.3: "Extra Large",
};

const normalizeScale = (value) => {
  const parsed = Number(value);
  return SCALE_VALUES.includes(parsed) ? parsed : 1;
};

const readStoredScale = () => {
  if (typeof window === "undefined") return 1;
  try {
    return normalizeScale(localStorage.getItem(STORAGE_KEY));
  } catch (_err) {
    return 1;
  }
};

export default function useContentFontScale() {
  const [scale, setScaleState] = useState(readStoredScale);
  const scaleRef = useRef(scale);

  useEffect(() => {
    scaleRef.current = scale;
  }, [scale]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const handleCustomEvent = (event) => {
      const next = normalizeScale(event?.detail ?? readStoredScale());
      setScaleState((prev) => (prev === next ? prev : next));
    };

    const handleStorage = (event) => {
      if (event.key !== STORAGE_KEY) return;
      const next = normalizeScale(event.newValue);
      setScaleState((prev) => (prev === next ? prev : next));
    };

    window.addEventListener("contentFontScaleChange", handleCustomEvent);
    window.addEventListener("storage", handleStorage);
    return () => {
      window.removeEventListener("contentFontScaleChange", handleCustomEvent);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  const setScale = useCallback((nextValue) => {
    const next = normalizeScale(nextValue);
    if (next === scaleRef.current) return;
    setScaleState(next);
    try {
      localStorage.setItem(STORAGE_KEY, String(next));
    } catch (_err) {
      // ignore storage errors
    }
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("contentFontScaleChange", { detail: next }));
    }
  }, []);

  const currentIndex = SCALE_VALUES.indexOf(scale);

  const increase = useCallback(() => {
    const next = SCALE_VALUES[Math.min(currentIndex + 1, SCALE_VALUES.length - 1)];
    setScale(next);
  }, [currentIndex, setScale]);

  const decrease = useCallback(() => {
    const next = SCALE_VALUES[Math.max(currentIndex - 1, 0)];
    setScale(next);
  }, [currentIndex, setScale]);

  const options = useMemo(
    () =>
      SCALE_VALUES.map((value) => ({
        value,
        label: SCALE_LABELS[value] || "Custom",
      })),
    []
  );

  return {
    scale,
    label: SCALE_LABELS[scale] || "Custom",
    options,
    setScale,
    increase,
    decrease,
    canIncrease: currentIndex < SCALE_VALUES.length - 1,
    canDecrease: currentIndex > 0,
  };
}
